import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import apiKeysService from '../../services/apiKeysService';

const HOOKS_DOCS_URL = 'https://github.com/Tymyan1/whatsurvey/blob/main/docs/api/hooks.md';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : 'Never');

/**
 * Settings → API keys: the credentials Zapier, Make, n8n or a merchant's own code use against
 * /api/hooks and /api/v1 instead of a browser's JWT session. A key's plaintext value is shown
 * exactly once, on creation.
 */
const ApiKeysSettings = () => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createdKey, setCreatedKey] = useState(null); // the plaintext value, shown once
  const [copied, setCopied] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState(null); // { id, label } | null
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState(null);

  const load = useCallback(async () => {
    if (!envId) return;
    setError(null);
    try {
      const list = await apiKeysService.list(envId, authenticatedFetch);
      setKeys(list || []);
    } catch (e) {
      setError(e.message || 'Failed to load API keys');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  useEffect(() => {
    if (!envId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  const openCreate = () => {
    setLabel('');
    setCreateError(null);
    setCreatedKey(null);
    setCopied(false);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const result = await apiKeysService.create(envId, label.trim(), authenticatedFetch);
      setCreatedKey(result.key);
      await load();
    } catch (e) {
      setCreateError(e.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the key stays visible for a manual copy.
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await apiKeysService.revoke(envId, revokeTarget.id, authenticatedFetch);
      setRevokeTarget(null);
      await load();
    } catch (e) {
      setRevokeError(e.message || 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  };

  if (!envId) {
    return <Alert severity="warning">Please select an environment first</Alert>;
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              API keys
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              An API key lets Zapier, Make, n8n or your own code read submissions and bookings and
              subscribe to instant notifications, without a browser session. See{' '}
              <Link href={HOOKS_DOCS_URL} target="_blank" rel="noopener noreferrer">
                the API docs
              </Link>{' '}
              for authentication, endpoints and signature verification.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            data-testid="create-api-key-button"
          >
            New key
          </Button>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }} data-testid="api-keys-error">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {keys.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No API keys yet.
              </Typography>
            )}

            {keys.map((key) => (
              <Box
                key={key.id}
                data-testid={`api-key-row-${key.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography variant="body1">
                    {key.label}{' '}
                    <Typography component="span" variant="body2" color="text.secondary">
                      ({key.keyPrefix}…)
                    </Typography>
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`Created ${formatDate(key.createdAt)}`} variant="outlined" />
                    <Chip size="small" label={`Last used ${formatDate(key.lastUsedAt)}`} variant="outlined" />
                    {key.revoked && <Chip size="small" label="Revoked" color="default" />}
                  </Stack>
                </Box>

                {!key.revoked && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setRevokeTarget({ id: key.id, label: key.label })}
                    data-testid={`revoke-button-${key.id}`}
                  >
                    Revoke
                  </Button>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>

      <Dialog open={createOpen} onClose={() => (creating ? null : setCreateOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>{createdKey ? 'API key created' : 'New API key'}</DialogTitle>
        <DialogContent>
          {createdKey ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Copy this key now. You will not be able to see it again.
              </DialogContentText>
              <Box className="ph-no-capture" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={createdKey}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                  data-testid="created-api-key-value"
                />
                <Tooltip title={copied ? 'Copied' : 'Copy'}>
                  <IconButton onClick={handleCopy} data-testid="copy-api-key-button">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Give the key a name so you remember what it is for, such as "Zapier" or "Make scenario".
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                label="Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="api-key-label-input"
              />
            </>
          )}
          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {createdKey ? (
            <Button onClick={() => setCreateOpen(false)} data-testid="close-created-key-button">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={() => setCreateOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                variant="contained"
                disabled={creating || !label.trim()}
                data-testid="confirm-create-api-key-button"
              >
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!revokeTarget} onClose={() => (revoking ? null : setRevokeTarget(null))}>
        <DialogTitle>Revoke this key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Anything using "{revokeTarget?.label}" will stop working immediately. This cannot be undone.
          </DialogContentText>
          {revokeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button onClick={handleRevoke} color="error" disabled={revoking} data-testid="confirm-revoke-button">
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ApiKeysSettings;
